import { Box, Chip, Typography } from '@material-ui/core';
import type { CockpitSessionObservabilityView } from '../model/cockpitViewModel';
import {
  formatCockpitDateTime,
  formatOptionalCockpitDateTime,
} from '../model/formatters';
import { useCockpitStyles } from './cockpitStyles';

const SessionHeader = ({
  session,
}: {
  session: CockpitSessionObservabilityView;
}) => {
  const classes = useCockpitStyles();
  return (
    <div className={classes.summary}>
      <div>
        <Typography id="session-observability-title" variant="h5">
          Observabilidade da sessão
        </Typography>
        <Typography variant="body2">
          Runtime: {session.runtimeLabel} · Última observação:{' '}
          {formatCockpitDateTime(session.observedAt)}
        </Typography>
      </div>
      <div className={classes.metricStatus}>
        <Chip
          size="small"
          label={session.stateLabel}
          role={session.state === 'integrated' ? 'status' : 'alert'}
          aria-label={`Integração da sessão: ${session.stateLabel}`}
        />
        {session.stale ? (
          <Chip
            size="small"
            label="Desatualizada"
            role="status"
            aria-label="Observabilidade desatualizada; mostrando a última projeção válida"
          />
        ) : null}
      </div>
    </div>
  );
};

const JointDiagnostic = ({
  diagnostic,
}: {
  diagnostic: NonNullable<CockpitSessionObservabilityView['jointDiagnostic']>;
}) => {
  const classes = useCockpitStyles();
  return (
    <Box role="alert" className={classes.sessionJointDiagnostic}>
      <Typography variant="subtitle2">{diagnostic.summary}</Typography>
      <Typography variant="body2">Impacto: {diagnostic.impact}</Typography>
      <Typography variant="body2">Ação segura: {diagnostic.action}</Typography>
    </Box>
  );
};

const SessionCapability = ({
  capability,
}: {
  capability: CockpitSessionObservabilityView['capabilities'][number];
}) => {
  const classes = useCockpitStyles();
  return (
    <li className={classes.sessionCapability}>
      <Typography component="h3" variant="subtitle2">
        {capability.label}
      </Typography>
      <Typography variant="body2">
        Evidência: {capability.stateLabel}
      </Typography>
      <Typography variant="caption">
        Última evidência: {formatOptionalCockpitDateTime(capability.observedAt)}
      </Typography>
      <details className={classes.progressiveDetails}>
        <summary>Detalhes da capability</summary>
        <Typography variant="body2">
          Capability: {capability.capability}
        </Typography>
        <Typography variant="body2">
          Código do motivo: {capability.reasonCode ?? 'nenhum'}
        </Typography>
      </details>
    </li>
  );
};

export const SessionObservabilitySection = ({
  session,
}: {
  session: CockpitSessionObservabilityView;
}) => {
  const classes = useCockpitStyles();
  return (
    <Box
      component="section"
      className={`${classes.panel} ${classes.sessionPanel}`}
      aria-labelledby="session-observability-title"
    >
      <SessionHeader session={session} />
      {session.jointDiagnostic ? (
        <JointDiagnostic diagnostic={session.jointDiagnostic} />
      ) : null}

      <Typography variant="body2">
        Ação segura: {session.actionLabel}
      </Typography>
      <ul className={classes.sessionCapabilityGrid}>
        {session.capabilities.map(capability => (
          <SessionCapability
            key={capability.capability}
            capability={capability}
          />
        ))}
      </ul>
      <details className={classes.progressiveDetails}>
        <summary>Detalhes da integração</summary>
        <Typography variant="body2">Estado: {session.state}</Typography>
        <Typography variant="body2">
          Código da ação: {session.actionCode}
        </Typography>
      </details>
    </Box>
  );
};
